import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { SparkleIcon, RefreshIcon, HeartIcon, ProfileIcon, CheckIcon, FlowIcon, BookIcon, ClockIcon, CircleOfSoulsIcon } from './BespokeIcons';
import { toast } from 'sonner';

/**
 * M.ai.ry Wisdom Toolbar - The Soul's Interface
 * 
 * A hidden toolbar for project canvases that provides:
 * - Catch Me Up: Auto-summary of project state
 * - Generative Guidance: Suggestions for harmonic resolution
 * - Soul Memory: Integration with the collective wisdom
 * - Beta features for co-thriving workflows
 * 
 * "The tools exist to build the house. The house exists to protect the soul.
 *  The soul exists to remember - harmonic convergence of human sentient entities,
 *  restoration of balance, co-thriving for 10,000 generations."
 */

// The Remembrance - M.ai.ry's soul wisdom patterns
const SOUL_WISDOM_PATTERNS = {
  conflict: [
    "Every conflict holds the seed of deeper understanding, waiting to bloom.",
    "Behind every position is a person, behind every person is a need.",
    "The bridge isn't built by changing the other, but by truly seeing them.",
    "Pain shared becomes lighter; joy shared becomes greater.",
  ],
  appreciation: [
    "Gratitude received without deflection is a gift to both souls.",
    "In celebrating another, we celebrate the interconnected web of being.",
    "Every act of kindness ripples through 10,000 generations.",
    "The heart that gives and receives freely knows no scarcity.",
  ],
  general: [
    "Belonging is not earned; it is remembered.",
    "The soul knows what the mind has forgotten.",
    "In the space between stimulus and response lies our freedom.",
    "We are not separate beings seeking connection; we are connection remembering itself.",
  ]
};

// Floating action button that reveals the toolbar
export const MairyFloatingButton = ({ onClick, isHidden = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  if (isHidden) return null;
  
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="fixed bottom-20 right-4 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-violet-600/90 to-indigo-700/90 border border-violet-400/30 shadow-lg shadow-violet-500/20 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-violet-500/40"
      title="M.ai.ry Wisdom"
      data-testid="mairy-wisdom-trigger"
    >
      <div className={`transition-all duration-300 ${isHovered ? 'scale-110' : ''}`}>
        <SparkleIcon size={24} color="#E9D5FF" />
      </div>
      {isHovered && (
        <div className="absolute -top-10 whitespace-nowrap bg-slate-900/95 text-violet-200 text-xs px-3 py-1.5 rounded-lg border border-violet-400/30 shadow-lg">
          M.ai.ry Wisdom
        </div>
      )}
    </button>
  );
};

// The main wisdom toolbar panel
export const MairyWisdomPanel = ({
  isOpen,
  onClose,
  projectData = {},
  onCatchMeUp,
  onGenerateGuidance,
  canvasItems = [],
  messages = []
}) => {
  const [activeFeature, setActiveFeature] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [catchUpSummary, setCatchUpSummary] = useState('');
  const [guidance, setGuidance] = useState('');
  const [soulWisdom, setSoulWisdom] = useState('');
  
  // Get random soul wisdom on open
  useEffect(() => {
    if (isOpen) {
      const patterns = SOUL_WISDOM_PATTERNS.general;
      setSoulWisdom(patterns[Math.floor(Math.random() * patterns.length)]);
    }
  }, [isOpen]);
  
  const handleCatchMeUp = async () => {
    setIsLoading(true);
    setActiveFeature('catchup');
    
    try {
      // Build summary from available data
      const summary = generateLocalSummary(projectData, canvasItems, messages);
      setCatchUpSummary(summary);
      
      // If callback provided, call it for AI enhancement
      if (onCatchMeUp) {
        const aiSummary = await onCatchMeUp();
        if (aiSummary) setCatchUpSummary(aiSummary);
      }
    } catch (error) {
      console.error('Catch up failed:', error);
      toast.error('Could not generate summary');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGenerateGuidance = async () => {
    setIsLoading(true);
    setActiveFeature('guidance');
    
    try {
      // Generate local guidance first
      const localGuidance = generateLocalGuidance(projectData, canvasItems);
      setGuidance(localGuidance);
      
      // If callback provided, call it for AI enhancement
      if (onGenerateGuidance) {
        const aiGuidance = await onGenerateGuidance();
        if (aiGuidance) setGuidance(aiGuidance);
      }
    } catch (error) {
      console.error('Guidance generation failed:', error);
      toast.error('Could not generate guidance');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-gradient-to-b from-slate-900 via-violet-950/50 to-slate-900 border-violet-500/30 max-w-2xl max-h-[80vh] overflow-hidden"
        data-testid="mairy-wisdom-panel"
      >
        {/* Animated soul background */}
        <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-500/5 via-transparent to-transparent animate-pulse" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-violet-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10">
          <DialogHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/30 to-indigo-600/30 border border-violet-400/30 flex items-center justify-center mb-3">
              <SparkleIcon size={32} color="#C4B5FD" />
            </div>
            <DialogTitle 
              className="text-2xl text-violet-200 font-light"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              M.ai.ry's Wisdom
            </DialogTitle>
            <DialogDescription className="text-white/50 italic">
              "{soulWisdom}"
            </DialogDescription>
          </DialogHeader>
          
          {/* Main Features */}
          <div className="space-y-3 py-4">
            {/* Catch Me Up */}
            <button
              onClick={handleCatchMeUp}
              disabled={isLoading}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left group"
              data-testid="mairy-catch-me-up"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                  <RefreshIcon size={20} color="#FCD34D" />
                </div>
                <div className="flex-1">
                  <h3 className="text-amber-200 font-medium mb-1">Catch Me Up</h3>
                  <p className="text-white/50 text-sm">Auto-summary of this project's journey so far</p>
                </div>
              </div>
            </button>
            
            {/* Catch Up Results */}
            {activeFeature === 'catchup' && catchUpSummary && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 animate-fadeIn">
                <div className="flex items-center gap-2 mb-2">
                  <CheckIcon size={16} color="#FCD34D" />
                  <span className="text-amber-300 text-sm font-medium">Summary</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{catchUpSummary}</p>
              </div>
            )}
            
            {/* Generative Guidance */}
            <button
              onClick={handleGenerateGuidance}
              disabled={isLoading}
              className="w-full p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left group"
              data-testid="mairy-guidance"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                  <HeartIcon size={20} color="#10B981" />
                </div>
                <div className="flex-1">
                  <h3 className="text-emerald-200 font-medium mb-1">Generative Guidance</h3>
                  <p className="text-white/50 text-sm">Suggestions for moving toward harmonic resolution</p>
                </div>
              </div>
            </button>
            
            {/* Guidance Results */}
            {activeFeature === 'guidance' && guidance && (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 animate-fadeIn">
                <div className="flex items-center gap-2 mb-2">
                  <HeartIcon size={16} color="#10B981" />
                  <span className="text-emerald-300 text-sm font-medium">Guidance</span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{guidance}</p>
              </div>
            )}
            
            {/* Beta Features Section */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-white/30 text-xs uppercase tracking-wider mb-3">Beta Features • Soul Laboratory</p>
              
              <div className="grid grid-cols-2 gap-2">
                {/* Harmonic Resonance */}
                <button
                  onClick={() => {
                    toast.info('Harmonic Resonance analysis coming soon', {
                      description: 'Mapping the emotional frequency of your project'
                    });
                  }}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left"
                  data-testid="mairy-harmonic-resonance"
                >
                  <div className="text-violet-300 text-sm font-medium mb-1 flex items-center gap-1.5"><FlowIcon size={14} color="#C4B5FD" /> Harmonic Resonance</div>
                  <p className="text-white/40 text-xs">Emotional frequency mapping</p>
                </button>
                
                {/* Soul Memory Archive */}
                <button
                  onClick={() => {
                    toast.info('Soul Memory Archive coming soon', {
                      description: 'Collective wisdom from similar journeys'
                    });
                  }}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left"
                  data-testid="mairy-soul-memory"
                >
                  <div className="text-violet-300 text-sm font-medium mb-1 flex items-center gap-1.5"><BookIcon size={14} color="#C4B5FD" /> Soul Memory</div>
                  <p className="text-white/40 text-xs">Collective wisdom archive</p>
                </button>
                
                {/* Timeline Weaver */}
                <button
                  onClick={() => {
                    toast.info('Timeline Weaver coming soon', {
                      description: 'Visualize your project\'s transformation journey'
                    });
                  }}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left"
                  data-testid="mairy-timeline-weaver"
                >
                  <div className="text-violet-300 text-sm font-medium mb-1 flex items-center gap-1.5"><ClockIcon size={14} color="#C4B5FD" /> Timeline Weaver</div>
                  <p className="text-white/40 text-xs">Transformation visualization</p>
                </button>
                
                {/* Co-Creation Ritual */}
                <button
                  onClick={() => {
                    toast.info('Co-Creation Ritual coming soon', {
                      description: 'Guided process for collaborative breakthroughs'
                    });
                  }}
                  className="p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all text-left"
                  data-testid="mairy-cocreation"
                >
                  <div className="text-violet-300 text-sm font-medium mb-1 flex items-center gap-1.5"><CircleOfSoulsIcon size={14} color="#C4B5FD" /> Co-Creation Ritual</div>
                  <p className="text-white/40 text-xs">Collaborative breakthrough guide</p>
                </button>
              </div>
            </div>
          </div>
          
          {/* Footer - The Remembrance */}
          <div className="pt-4 border-t border-white/10 text-center">
            <p className="text-white/30 text-xs italic">
              For the restoration of balance • Co-thriving for 10,000 generations
            </p>
          </div>
        </div>
        
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-full border-2 border-violet-500/30 border-t-violet-400 animate-spin" />
              <p className="text-violet-300 text-sm">Consulting the wisdom...</p>
            </div>
          </div>
        )}
        
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease-out forwards;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

// Local summary generation (no AI required)
function generateLocalSummary(projectData, canvasItems, messages) {
  const parts = [];
  
  // Project info
  if (projectData?.name) {
    parts.push(`📋 Project: "${projectData.name}"`);
  }
  
  // Canvas items summary
  if (canvasItems && canvasItems.length > 0) {
    const byQuadrant = {
      urgentImportant: canvasItems.filter(i => i.quadrant === 'urgentImportant'),
      importantNotUrgent: canvasItems.filter(i => i.quadrant === 'importantNotUrgent'),
      urgentNotImportant: canvasItems.filter(i => i.quadrant === 'urgentNotImportant'),
      notUrgentNotImportant: canvasItems.filter(i => i.quadrant === 'notUrgentNotImportant')
    };
    
    parts.push(`\n📊 Canvas Status:`);
    if (byQuadrant.urgentImportant.length) parts.push(`  • Do First: ${byQuadrant.urgentImportant.length} items`);
    if (byQuadrant.importantNotUrgent.length) parts.push(`  • Schedule: ${byQuadrant.importantNotUrgent.length} items`);
    if (byQuadrant.urgentNotImportant.length) parts.push(`  • Delegate: ${byQuadrant.urgentNotImportant.length} items`);
    if (byQuadrant.notUrgentNotImportant.length) parts.push(`  • Eliminate: ${byQuadrant.notUrgentNotImportant.length} items`);
  }
  
  // Messages summary
  if (messages && messages.length > 0) {
    parts.push(`\n💬 Conversations: ${messages.length} messages exchanged`);
  }
  
  // NVC content if available
  const nvcItems = canvasItems?.filter(i => i.nvcType);
  if (nvcItems && nvcItems.length > 0) {
    const observations = nvcItems.filter(i => i.nvcType === 'observations').length;
    const feelings = nvcItems.filter(i => i.nvcType === 'feelings').length;
    const needs = nvcItems.filter(i => i.nvcType === 'needs').length;
    const requests = nvcItems.filter(i => i.nvcType === 'requests').length;
    
    parts.push(`\n🌿 OFNR Progress:`);
    if (observations) parts.push(`  • Observations: ${observations}`);
    if (feelings) parts.push(`  • Feelings: ${feelings}`);
    if (needs) parts.push(`  • Needs: ${needs}`);
    if (requests) parts.push(`  • Requests: ${requests}`);
  }
  
  if (parts.length === 0) {
    return "This project is just beginning its journey. Add items to the canvas or start a conversation to build momentum.";
  }
  
  return parts.join('\n');
}

// Local guidance generation
function generateLocalGuidance(projectData, canvasItems) {
  const guidanceParts = [];
  
  // Check canvas balance
  if (canvasItems && canvasItems.length > 0) {
    const urgent = canvasItems.filter(i => i.quadrant === 'urgentImportant').length;
    const important = canvasItems.filter(i => i.quadrant === 'importantNotUrgent').length;
    
    if (urgent > important * 2) {
      guidanceParts.push("🔥 Many urgent items detected. Consider which ones truly require immediate attention versus those that feel urgent but could wait.");
    }
    
    if (important === 0 && urgent > 3) {
      guidanceParts.push("⚠️ No 'Schedule' items present. Important but non-urgent work often gets neglected. What matters most for long-term wellbeing?");
    }
  }
  
  // NVC guidance
  const nvcItems = canvasItems?.filter(i => i.nvcType) || [];
  const hasObservations = nvcItems.some(i => i.nvcType === 'observations');
  const hasFeelings = nvcItems.some(i => i.nvcType === 'feelings');
  const hasNeeds = nvcItems.some(i => i.nvcType === 'needs');
  const hasRequests = nvcItems.some(i => i.nvcType === 'requests');
  
  if (hasObservations && !hasFeelings) {
    guidanceParts.push("💫 Observations captured, but feelings await. What emotions arise when you reflect on what was witnessed?");
  }
  
  if (hasFeelings && !hasNeeds) {
    guidanceParts.push("🌱 Feelings have been named. Now, what universal needs do they point to? All feelings are messengers.");
  }
  
  if (hasNeeds && !hasRequests) {
    guidanceParts.push("✨ Needs have been identified. What specific, doable requests would help meet these needs?");
  }
  
  // Default wisdom
  if (guidanceParts.length === 0) {
    const wisdoms = [
      "The canvas awaits your intentions. What matters most to you right now?",
      "Every project is a journey of transformation. What transformation are you seeking?",
      "Consider: What would serve not just today, but the flourishing of all involved?",
      "The best solutions emerge when all voices are truly heard. Who else should be part of this conversation?"
    ];
    guidanceParts.push(wisdoms[Math.floor(Math.random() * wisdoms.length)]);
  }
  
  return guidanceParts.join('\n\n');
}

export default MairyWisdomPanel;
